/**
 * «Facturar» en un presupuesto ACEPTADO: «Crear factura» y, en Business, los
 * anticipos — `OD-0053 D-07`, piezas 1, 2 y 4.
 *
 * - **No calcula nada.** El porcentaje facturado, lo pendiente y si se puede
 *   hacer otro anticipo o la factura final los dice la API
 *   (`GET /quotes/:id/advances`): la pantalla no suma porcentajes ni compara
 *   planes (`URS §105`, `§129`).
 * - Con anticipos, «Facturar el resto» ocupa el sitio de «Crear factura»: el
 *   segundo copiaría el 100 % y repercutiría dos veces el IVA de lo anticipado
 *   (`D-08`). La API lo rechaza igual; aquí ni se ofrece.
 * - Clave de idempotencia fija por formulario (`TDS-000 §5.1`).
 */
import { useCallback, useEffect, useState } from "react";
import { api, ESTADO_VALIDACION, type AnticiposPresupuesto, type DatosAnticipo, type ErrorApi, type Presupuesto } from "../api/cliente";
import { t } from "../i18n/es";
import { importeLegible, sinPuntosDeMiles } from "../ui/formato";
import { Aviso, Boton, Campo, Selector, Tarjeta } from "../ui/primitivas";

/** El día de hoy en Madrid, AAAA-MM-DD: sólo para proponer la fecha del cobro; la valida la API. */
function hoy(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Madrid" }).format(new Date());
}

type Modo = "PERCENT" | "AMOUNT";

/** La guía plegada (`D-07`, pieza 2): quien ya lo sabe no tiene por qué leerla cada vez. */
function GuiaAnticipos() {
  return (
    <details className="desglose guia-anticipos">
      <summary>{t.anticipos.guia.titulo}</summary>
      {t.anticipos.guia.secciones.map((s) => (
        <section key={s.titulo}>
          <h4>{s.titulo}</h4>
          {s.parrafos.map((p) => (
            <p key={p}>{p}</p>
          ))}
          {s.puntos.length > 0 ? (
            <ul>
              {s.puntos.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          ) : null}
        </section>
      ))}
    </details>
  );
}

export function TarjetaFacturarPresupuesto({
  quote,
  hayConexion,
  ocupadoFuera,
  onCrearFactura,
  onAbrirFactura,
  onSesionCaducada,
}: {
  quote: Presupuesto;
  hayConexion: boolean;
  /** La pantalla está guardando o facturando por su cuenta. */
  ocupadoFuera: boolean;
  /** «Crear factura», que vive en la pantalla del presupuesto (TDS-CORE Parte 3 §2). */
  onCrearFactura: () => void;
  onAbrirFactura: (id: string) => void;
  onSesionCaducada: () => void;
}) {
  const [estado, setEstado] = useState<AnticiposPresupuesto | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [modo, setModo] = useState<Modo>("PERCENT");
  const [valor, setValor] = useState("");
  const [fechaCobro, setFechaCobro] = useState(hoy);
  const [errores, setErrores] = useState<Readonly<Record<string, string>>>({});
  const [mensaje, setMensaje] = useState<{ tono: "error" | "aviso"; texto: string } | null>(null);
  const [ocupado, setOcupado] = useState(false);
  // Una por formulario abierto: reintentar el mismo envío no crea dos anticipos.
  const [claveAnticipo, setClaveAnticipo] = useState(() => crypto.randomUUID());
  const [claveResto] = useState(() => crypto.randomUUID());

  const cargar = useCallback(async (): Promise<void> => {
    const r = await api.anticiposPresupuesto(quote.id);
    if (r.ok) return setEstado(r.datos);
    if (r.estado === 401) return onSesionCaducada();
    // Sin la respuesta, la tarjeta se comporta como antes: sólo «Crear factura».
    setEstado(null);
  }, [quote.id, onSesionCaducada]);

  useEffect(() => {
    void cargar();
  }, [cargar, quote.status]);

  function rechazo(r: { estado: number; error: ErrorApi | null }): void {
    if (r.estado === 401) return onSesionCaducada();
    if (r.estado === ESTADO_VALIDACION && r.error !== null) {
      setErrores(r.error.campos);
      return setMensaje({ tono: "error", texto: r.error.message });
    }
    if (r.estado === 402) return setMensaje({ tono: "aviso", texto: t.anticipos.soloBusiness });
    const bloqueo = r.error?.motivo === null || r.error?.motivo === undefined ? undefined : t.anticipos.bloqueos[r.error.motivo];
    if (r.estado === 409 && bloqueo !== undefined) return setMensaje({ tono: "aviso", texto: bloqueo });
    if ((r.estado === 409 || r.estado === 429) && r.error !== null) {
      return setMensaje({ tono: "aviso", texto: `${r.error.message} ${r.error.action ?? ""}`.trim() });
    }
    setMensaje({ tono: "error", texto: r.estado === 0 ? t.comun.sinRed : t.comun.errorGenerico });
  }

  async function crearAnticipo(): Promise<void> {
    setMensaje(null);
    setErrores({});
    setOcupado(true);
    const datos: DatosAnticipo = modo === "PERCENT" ? { percent: valor.trim(), operationDate: fechaCobro } : { amount: sinPuntosDeMiles(valor), operationDate: fechaCobro };
    const r = await api.facturarAnticipo(quote.id, datos, claveAnticipo);
    setOcupado(false);
    if (!r.ok) return rechazo(r);
    setClaveAnticipo(crypto.randomUUID());
    onAbrirFactura(r.datos.id);
  }

  async function facturarResto(): Promise<void> {
    setMensaje(null);
    setOcupado(true);
    const r = await api.facturarResto(quote.id, claveResto);
    setOcupado(false);
    if (r.ok) return onAbrirFactura(r.datos.id);
    const existente = r.error?.detalles.invoiceId;
    if (r.estado === 409 && r.error?.motivo === "ALREADY_INVOICED" && typeof existente === "string") return onAbrirFactura(existente);
    rechazo(r);
  }

  const errorDe = (campo: string): string | null =>
    errores[campo] === undefined ? null : (t.anticipos.errores[errores[campo]] ?? t.comun.errorGenerico);
  const desactivado = ocupado || ocupadoFuera || !hayConexion;
  const conAnticipos = estado !== null && estado.advances.length > 0;
  const motivoBloqueo = estado?.restBlockedReason ?? null;

  return (
    <Tarjeta titulo={t.presupuesto.facturarTitulo}>
      {conAnticipos && estado !== null ? (
        <>
          {/* D-07, pieza 4: el estado, y «Facturar el resto» en lugar de «Crear factura». */}
          <p>
            {t.anticipos.estado(
              estado.advances.map((a) => t.anticipos.fila(a.percent, a.baseTotal === null ? null : importeLegible(a.baseTotal), a.number)).join("; "),
              estado.percentPending,
            )}
          </p>
          <ul className="lista-pendientes">
            {estado.advances.map((a) => (
              <li key={a.invoiceId}>
                <span>{t.anticipos.fila(a.percent, a.baseTotal === null ? null : importeLegible(a.baseTotal), a.number)}</span>
                <Boton variante="secundario" onClick={() => onAbrirFactura(a.invoiceId)}>
                  {t.anticipos.abrir}
                </Boton>
              </li>
            ))}
          </ul>
          {estado.canInvoiceRest ? (
            <>
              <p className="ayuda-neutra">{t.anticipos.restoDetalle}</p>
              <Boton onClick={() => void facturarResto()} deshabilitado={desactivado}>
                {t.anticipos.resto}
              </Boton>
            </>
          ) : motivoBloqueo !== null && t.anticipos.bloqueos[motivoBloqueo] !== undefined ? (
            <Aviso tono="info">{t.anticipos.bloqueos[motivoBloqueo]}</Aviso>
          ) : null}
          {estado.finalInvoiceId !== null ? (
            <Boton variante="secundario" onClick={() => onAbrirFactura(estado.finalInvoiceId as string)}>
              {t.anticipos.abrirFinal}
            </Boton>
          ) : null}
        </>
      ) : (
        <>
          <p className="ayuda-neutra">{t.presupuesto.facturarDetalle}</p>
          <Boton onClick={onCrearFactura} deshabilitado={desactivado}>
            {t.presupuesto.facturar}
          </Boton>
        </>
      )}

      {/* D-07, piezas 1 y 2: el anticipo, junto a «Crear factura». Sólo si el plan lo incluye (D-08 B). */}
      {estado !== null && estado.included && estado.canCreateAdvance ? (
        <>
          <h3>{t.anticipos.titulo}</h3>
          <p className="ayuda-neutra">{t.anticipos.invitacion}</p>
          <GuiaAnticipos />
          {!abierto ? (
            <Boton variante="secundario" onClick={() => setAbierto(true)} deshabilitado={desactivado}>
              {conAnticipos ? t.anticipos.otro : t.anticipos.boton}
            </Boton>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void crearAnticipo();
              }}
              noValidate
            >
              <Selector<Modo>
                id="ant-modo"
                etiqueta={t.anticipos.titulo}
                valor={modo}
                opciones={[
                  { valor: "PERCENT", texto: t.anticipos.porPorcentaje },
                  { valor: "AMOUNT", texto: t.anticipos.porImporte },
                ]}
                onChange={(v) => {
                  setModo(v);
                  setErrores({});
                }}
              />
              <div className="fila-campos">
                <Campo
                  id="ant-valor"
                  etiqueta={modo === "PERCENT" ? t.anticipos.porcentaje : t.anticipos.importe}
                  valor={valor}
                  onChange={setValor}
                  ayuda={modo === "AMOUNT" ? t.anticipos.ayudaImporte : null}
                  autoComplete="off"
                  error={errorDe(modo === "PERCENT" ? "percent" : "amount")}
                />
                <Campo
                  id="ant-fecha"
                  etiqueta={t.anticipos.fechaCobro}
                  valor={fechaCobro}
                  onChange={setFechaCobro}
                  ayuda={t.anticipos.ayudaFechaCobro}
                  tipo="date"
                  autoComplete="off"
                  error={errorDe("operationDate")}
                />
              </div>
              <div className="acciones">
                <Boton tipo="submit" deshabilitado={desactivado}>
                  {ocupado ? t.anticipos.creando : t.anticipos.crear}
                </Boton>
                <Boton variante="secundario" onClick={() => setAbierto(false)}>
                  {t.anticipos.cancelar}
                </Boton>
              </div>
            </form>
          )}
        </>
      ) : null}

      {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}
    </Tarjeta>
  );
}
